import { useEffect } from 'react';

import {
  addSlotEventListener,
  loadGPTAds,
  removeSlotEventListener,
  unloadGPTAds,
} from '@helpers/ads';

import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';

interface AdSlotConfig {
  adUnitPath: string;
  size: googletag.GeneralSize;
  htmlDivId: string;
  targetParams: Record<string, string>;
}

export function useGPTAdsInitialization(
  hotelMetaSearch: HotelMetaSearch,
  isMapVisible: boolean,
  targetParams: { [key: string]: string } | undefined,
  isScreenBetween1200And1340: boolean,
  isScreenAbove1340: boolean,
  setGoogleSlots: (value: Array<googletag.Slot> | undefined) => void,
  setHasMrecOrStickyAds: (value: boolean) => void,
  setTopLeaderboardAdsHeight: (value: number) => void,
  setMiddleLeaderboardAdsHeight: (value: number) => void,
  setBottomLeaderboardAdsHeight: (value: number) => void,
  handleSlotRenderEnded: (event: googletag.events.SlotRenderEndedEvent) => void,
) {
  const initializeAds = async (slotsConfigurations: Array<AdSlotConfig>): Promise<void> => {
    const slots = await loadGPTAds(slotsConfigurations, true, true, false, true, {
      fetchMarginPercent: 30,
      renderMarginPercent: 30,
      mobileScaling: 1,
    });
    addSlotEventListener('slotRenderEnded', handleSlotRenderEnded);

    setGoogleSlots(slots);
  };

  useEffect(() => {
    if (!!hotelMetaSearch.search && !isMapVisible && targetParams) {
      const slotsConfigurations: Array<AdSlotConfig> = [
        {
          adUnitPath: '/1033625/top_leaderboard',
          size: [[728, 90]],
          htmlDivId: 'top_leaderboard',
          targetParams,
        },
        {
          adUnitPath: '/1033625/middle_leaderboard',
          size: [[728, 90]],
          htmlDivId: 'middle_leaderboard',
          targetParams,
        },
        {
          adUnitPath: '/1033625/bottom_leaderboard',
          size: [[728, 90]],
          htmlDivId: 'bottom_leaderboard',
          targetParams,
        },
        {
          adUnitPath: '/1033625/desktop_native_hotels_1',
          size: [[728, 90], 'fluid'],
          htmlDivId: 'native_top_ad',
          targetParams,
        },
        {
          adUnitPath: '/1033625/desktop_native_hotels_2',
          size: [[728, 90], 'fluid'],
          htmlDivId: 'native_middle_ad',
          targetParams,
        },
        {
          adUnitPath: '/1033625/desktop_native_hotels_3',
          size: [[728, 90], 'fluid'],
          htmlDivId: 'native_bottom_ad',
          targetParams,
        },
        {
          adUnitPath: '/1033625/desktop_new_hotel_in_town',
          size: ['fluid'],
          htmlDivId: 'new_hotel_in_town',
          targetParams,
        },
      ];

      if (isScreenBetween1200And1340) {
        slotsConfigurations.push({
          adUnitPath: '/1033625/skyscraper',
          size: [[160, 600]],
          htmlDivId: 'skyscraper',
          targetParams,
        });
      }

      if (isScreenAbove1340) {
        slotsConfigurations.push(
          {
            adUnitPath: '/1033625/mrec_1',
            size: [
              [300, 250],
              [300, 600],
              [160, 600],
            ],
            htmlDivId: 'mrec_1',
            targetParams,
          },
          {
            adUnitPath: '/1033625/mrec_2',
            size: [[300, 250]],
            htmlDivId: 'mrec_2',
            targetParams,
          },
          {
            adUnitPath: '/1033625/desktop_sticky_ads',
            size: [
              [300, 250],
              [300, 600],
            ],
            htmlDivId: 'sticky_top',
            targetParams,
          },
          {
            adUnitPath: '/1033625/desktop_sticky_ads_bottom',
            size: [[300, 250]],
            htmlDivId: 'sticky_bottom',
            targetParams,
          },
        );
      }

      initializeAds(slotsConfigurations);
    }

    return () => {
      if (!targetParams) {
        setGoogleSlots(undefined);
        setHasMrecOrStickyAds(false);
        setTopLeaderboardAdsHeight(0);
        setMiddleLeaderboardAdsHeight(0);
        setBottomLeaderboardAdsHeight(0);
        removeSlotEventListener('slotRenderEnded', handleSlotRenderEnded);
        unloadGPTAds();
      }
    };
  }, [hotelMetaSearch, isMapVisible, targetParams, isScreenAbove1340, isScreenBetween1200And1340]);
}
